import * as actions from './actions'
import type { Action } from './types'
import type { CachedReport, Report } from 'types'
import type { InlineUpdates } from './inlineUpdates'

import { setInlineUpdate } from './inlineUpdates'
import { prepareCacheReports } from './helpers'

export type State = {
  data: Report[];
  cachedReports: CachedReport[];
  facets: Record<any, any>;
  allFacets: Record<any, any> | null;
  filter: Record<any, any>;
  errors: any[];
  sort: string;
  page: {
    offset: number;
    limit: number;
  };
  total: number;
  inlineUpdates: InlineUpdates;
  dataFetched: boolean;
  isFetchingReports: boolean;
};

export const initalFilterState = {
  page: { offset: 0, limit: 50 },
  filter: {},
  sort: '-generation_time'
} as const

export const initialState = {
  data: [],
  facets: {},
  allFacets: null,
  errors: [],
  total: 0,
  inlineUpdates: {},
  dataFetched: false,
  isFetchingReports: false,
  cachedReports: [],
  ...initalFilterState
} as const

const reducer = (state: State, action: Action) => {
// @ts-expect-error - TS2322 - readonly initialState not assignable to mutable State.
  if (state === undefined) { state = initialState }
  switch (action.type) {
    case actions.CACHE_MORE_REPORTS_SUCCESS:
      const { data: moreReports = [] } = action
      return {
        ...state,
        cachedReports: prepareCacheReports(moreReports, state.cachedReports, state.sort)
      }
    case actions.CACHE_MORE_REPORTS_FAILURE:
      return { ...state, errors: action.errors }
    case actions.API_REPORTS_INDEX:
      return { ...state, errors: [], dataFetched: false, isFetchingReports: true }
    case actions.API_REPORTS_INDEX_SUCCESS:
      const { data, meta } = action
      const nextState = {
        ...state,
        dataFetched: true,
        isFetchingReports: false,
        errors: []
      } as const
      if (data && meta) {
        return {
          ...nextState,
          data,
          total: meta.total,
          cachedReports: prepareCacheReports(data, [], state.sort)
        }
      }
      return nextState
    case actions.API_REPORTS_FACETS_INDEX_SUCCESS:
      const { data: facets } = action
      if (facets) {
        return {
          ...state,
          facets
        }
      }
      return state
    case actions.API_REPORTS_ALL_FACETS_INDEX:
      return { ...state, allFacets: null }
    case actions.API_REPORTS_ALL_FACETS_INDEX_FAILURE:
      return { ...state, allFacets: {} }
    case actions.API_REPORTS_ALL_FACETS_INDEX_SUCCESS:
      const { data: allFacets } = action
      if (allFacets) {
        return {
          ...state,
          allFacets
        }
      }
      return state
    case actions.FETCH_REPORT_DETAIL_SUCCESS:
      const { data: report } = action
      const index = state.data.findIndex(item => item.id === report.id)
      if (index > -1) {
        const newData = [
          ...state.data.slice(0, index),
          report,
          ...state.data.slice(index + 1)
        ]
        return {
          ...state,
          data: newData
        }
      } else {
        return state
      }
    case actions.API_REPORTS_INDEX_FAILURE:
      return {
        ...state,
        isFetchingReports: false,
        errors: action.errors.length ? action.errors : ['Failed to load'],
      }
    case actions.UPDATE_REPORT_USER_STATUS_FAILURE:
      return { ...state, errors: action.errors }
    case actions.API_REPORTS_SET_ALL_FILTERS:
      const { filter, sort, page } = action
      return { ...state, filter, sort, page, isFetchingReports: true }
    case actions.SET_SINGLE_REPORT_REVIEW_STATUS:
      return {
        ...state,
        data: state.data.map<Report>(
          (report) => report.id === action.payload.reportId ? {...report, reviewStatus: action.payload.reviewStatus} : report,
        )
      }
    case actions.SET_SINGLE_REPORT_FOLLOW_UP_LABEL:
      return {
        ...state,
        data: state.data.map<Report>((report) =>
          report.id === action.payload.reportId
            ? {
              ...report,
              followUpLabel: action.payload.followUpLabel,
              customFollowUpLabel: action.payload.customFollowUpLabel,
              labelNote: action.payload.labelNote,
            }
            : report)
      }
    case actions.SET_SINGLE_REPORT_USER_STATUS:
      return {
        ...state,
        data: state.data.map<Report>((report) =>
          report.id === action.payload.reportId
            ? { ...report, userStatus: action.payload.userStatus }
            : report)
      }
    case actions.INLINE_UPDATE_BEGIN:
      return {
        ...state,
        inlineUpdates: setInlineUpdate(
          action.payload.reportId,
          action.payload.field,
          { status: 'pending' },
          state.inlineUpdates,
        ),
      }
    case actions.INLINE_UPDATE_SUCCESS:
      return {
        ...state,
        inlineUpdates: setInlineUpdate(
          action.payload.reportId,
          action.payload.field,
          null,
          state.inlineUpdates,
        ),
      }
    case actions.INLINE_UPDATE_FAILURE:
      return {
        ...state,
        inlineUpdates: setInlineUpdate(
          action.payload.reportId,
          action.payload.field,
          { status: 'error', error: action.payload.error },
          state.inlineUpdates,
        ),
      }
    default:
      return state
  }
}

export default reducer
